import React, { useState } from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography, IconButton, useMediaQuery, createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Church as ChurchIcon,
  MusicNote as MusicIcon,
  SupportAgent as SupportIcon,
  Menu as MenuIcon,
  ExitToApp as ExitToAppIcon,
  Payment as PaymentIcon,
  NotificationsActive as NotificationsIcon,
  History as HistoryIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 260;

// Create a light theme specifically for the super admin dashboard
const lightAdminTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#a259ff', // keeping the Prelude purple
      light: '#b882ff',
      dark: '#7e3ed6',
    },
    background: {
      default: '#f4f6f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        }
      }
    }
  },
});

const SuperAdminLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const isMobile = useMediaQuery(lightAdminTheme.breakpoints.down('md'));

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
    { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
    { text: 'Churches', icon: <ChurchIcon />, path: '/admin/churches' },
    { text: 'Subscriptions', icon: <PaymentIcon />, path: '/admin/subscriptions' },
    { text: 'Push Notifications', icon: <NotificationsIcon />, path: '/admin/notifications' },
    { text: 'Global Songs', icon: <MusicIcon />, path: '/admin/songs' },
    { text: 'Support Tickets', icon: <SupportIcon />, path: '/admin/support' },
    { text: 'Audit Logs', icon: <HistoryIcon />, path: '/admin/audit-logs' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ 
          width: 36, 
          height: 36, 
          borderRadius: '10px', 
          background: 'linear-gradient(135deg, #a259ff 0%, #ff6b35 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '18px'
        }}>
          P
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.5px' }}>
          Prelude Staff
        </Typography>
      </Box>

      <List sx={{ px: 2, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem 
              button 
              key={item.text} 
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                borderRadius: '12px',
                mb: 1,
                bgcolor: isActive ? 'rgba(162, 89, 255, 0.1)' : 'transparent',
                color: isActive ? '#a259ff' : '#666',
                '&:hover': {
                  bgcolor: isActive ? 'rgba(162, 89, 255, 0.15)' : 'rgba(0,0,0,0.04)',
                }
              }}
            >
              <ListItemIcon sx={{ color: isActive ? '#a259ff' : '#888', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '14.5px'
                }} 
              />
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <ListItem 
          button 
          onClick={logout}
          sx={{
            borderRadius: '12px',
            color: '#d32f2f',
            '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' }
          }}
        >
          <ListItemIcon sx={{ color: '#d32f2f', minWidth: 40 }}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary="Exit Admin" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={lightAdminTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        
        {/* Mobile App Bar */}
        {isMobile && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, left: 0, right: 0, 
            height: 60, 
            bgcolor: 'white', 
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            zIndex: 1100,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ color: '#a259ff' }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 700, color: '#1a1a1a' }}>
              Prelude Staff
            </Typography>
          </Box>
        )}

        {/* Sidebar */}
        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none', boxShadow: '4px 0 24px rgba(0,0,0,0.05)' },
              }}
            >
              {drawer}
            </Drawer>
          ) : (
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #eaeaea' },
              }}
              open
            >
              {drawer}
            </Drawer>
          )}
        </Box>

        {/* Main Content */}
        <Box
          component="main"
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4 }, 
            mt: { xs: 8, md: 0 },
            maxWidth: '100vw',
            overflow: 'hidden'
          }}
        >
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SuperAdminLayout;
